// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, first, flatMap, last, map, tap } from 'rxjs/operators';

import { LMModelMultiPartFields, LMModelXLMultiPartFields, LMXLFileUploadEvent } from '@form/model/lm-model';
import { LMFileUploadProgressComponent } from './lm-file-upload-progress.component';
import { LMBusyIndicatorService } from '../lm-busyindicator.service';
import { LMMultiPartFields } from '@app/transaction';
import { LMXLFileUploadService } from '@services/file-upload/lm-xlfile-upload.service';
import { LMModalWrapperService, LMUrlUtilService } from '@app/services';

@Injectable()
export class LMFileUploadService {
  private EXTRA_JSON_DATA = 'extraJSONData';

  //default max-post-size in bytes
  // TODO: It will be better to have this in a config param
  private MAX_POST_SIZE_CONSTANT = 104857600;
  // Chunk size is slightly less than max_post_size to account for other items such as unique id and part number for upload.
  private XLFILE_CHUNK_SIZE = 104000000;

  constructor(
    private http: HttpClient,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmXLFileUploadService: LMXLFileUploadService,
    private lmModalWrapperService: LMModalWrapperService) {}

  public rotateOrFlip(ctx: CanvasRenderingContext2D, orientation: number, orientationOption: string, width: number, height: number) {
    // rotate or flip according to orientation
    switch (orientation) {
      case 0:
        if (orientationOption && orientationOption === 'portrait') {
          this.translatePortrait(ctx, width, height);
        } else if (orientationOption && orientationOption === 'landscape') {
          ctx.translate(height / 3.53, width / 3.53);
        }
        break;
      case 2:
        this.translatePortrait(ctx, width, height);
        this.flip(ctx);
        break;
      case 4:
        this.translatePortrait(ctx, width, height);
        this.flip(ctx);
        this.rotate180(ctx);
        break;
      case 3:
        this.translatePortrait(ctx, width, height);
        this.rotate180(ctx);
        break;
      case 5:
        this.translateLandscape(ctx, width, height);
        this.flip(ctx);
        this.rotate90(ctx, 'clockwise');
        break;
      case 6:
        if (orientationOption && orientationOption === 'portrait') {
          this.translatePortrait(ctx, width, height);
          this.rotate90(ctx, 'clockwise');
          ctx.scale(height / width, width / height);
        } else {
          this.translateLandscape(ctx, width, height);
          this.rotate90(ctx, 'clockwise');
        }
        break;
      case 7:
        this.translateLandscape(ctx, width, height);
        this.flip(ctx);
        this.rotate90(ctx, 'counterclockwise');
        break;
      case 8:
        this.translateLandscape(ctx, width, height);
        this.rotate90(ctx, 'counterclockwise');
        break;
      default: break;
    }
  }

  private flip(context) {
    context.scale(-1, 1);
  }

  private rotate90(context, where) {
    const degrees = where === 'clockwise' ? 90 : -90;
    context.rotate(degrees * Math.PI / 180);
  }

  private rotate180(context) {
    context.rotate(Math.PI);
  }

  private translatePortrait(context, width, height) {
    context.translate(width / 2, height / 2);
  }

  private translateLandscape(context, width, height) {
    context.translate(height / 2, width / 2);
  }

  // Utility function to convert a canvas to a BLOB
  public dataURLToBlob(dataURL: string): Blob {
    const BASE64_MARKER = ';base64,';
    let parts: string[] = [];
    let contentType: string = null;
    let raw: string = null;

    if (dataURL.indexOf(BASE64_MARKER) === -1) {
      parts = dataURL.split(',');
      contentType = parts[0].split(':')[1];
      raw = parts[1];
      return new Blob([ raw ], { type: contentType });
    }
    parts = dataURL.split(BASE64_MARKER);
    contentType = parts[0].split(':')[1];
    raw = window.atob(parts[1]);
    const rawLength: number = raw.length;
    const uInt8Array: Uint8Array = new Uint8Array(rawLength);
    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }
    return new Blob([ uInt8Array ] as BlobPart[], { type: contentType });
  }

  public makeTemplateFileRequest(panelId: string, formName: string, url: string, filesList: Array<LMModelMultiPartFields>, data: any): Observable<any> {
    const templateFormName = panelId + '_' + formName + '_';
    const formData: FormData = new FormData();
    let totalPostSize = 0;

    filesList.forEach((value: LMModelMultiPartFields, index: number) => {
      totalPostSize = this.buildFormData(templateFormName + index, formData, value);
    });

    return this.executeRequest(url, formData, data, totalPostSize);
  }

  public makeAttachmentListFileRequest(listName: string, url: string, files: LMMultiPartFields, data: any): Observable<any> {
    const formData: FormData = new FormData();
    const totalPostSize = this.buildFormData(listName, formData, files);
    return this.executeRequest(url, formData, data, totalPostSize);
  }

  public makeFileRequest(formName: string, url: string, files: LMModelMultiPartFields, data: any): Observable<any> {
    const formData: FormData = new FormData();
    const totalPostSize = this.buildFormData(formName, formData, files);
    return this.executeRequest(url, formData, data, totalPostSize);
  }

  public makeXLFileRequest(formName: string, url: string, files: LMModelMultiPartFields, xlFiles: LMModelXLMultiPartFields,
    data: any, xlFileUploadSubject$: Subject<LMXLFileUploadEvent>): Observable<any> {
    const formData: FormData = new FormData();
    if (this.getTotalPostSize(files, xlFiles, data) <= this.MAX_POST_SIZE_CONSTANT) {
      // If the size of the xlFiles is not large, do regular form post
      const totalPostSize = this.buildFormData(formName, formData, files, xlFiles);
      return this.executeRequest(url, formData, data, totalPostSize);
    }

    // Upload will not start until the form saves
    xlFileUploadSubject$.asObservable().subscribe((evt: LMXLFileUploadEvent) => {
      if (evt.uploadEventType === 'START_UPLOAD') {
        let startUploadUrl = url;
        if (evt.newResourceId) {
          // Note: evt.newResourceId is already encoded in form component
          // Switch to using the new resourceId since the BusinessObjectReference is required to update the field on complete upload.
          startUploadUrl = this.lmUrlUtilService.switchResourceIdAfterCreate(url, evt.newResourceId);
        }
        this.lmXLFileUploadService.uploadXLFileChunks(startUploadUrl, xlFiles, evt.dataView, evt.fieldId, this.XLFILE_CHUNK_SIZE, xlFileUploadSubject$);
      }
    });

    //Update the form save request object with the ids of fields that has big file objects
    const formActionRequest = data.actionRequestArray[0];
    formActionRequest.xlFileFields = {};
    // Include the name of binary fields and number of chunks to expect.
    // Start upload will be called on save. The result of save will return the uniqueId to upload parts.
    Object.keys(xlFiles).forEach(fieldId => {
      formActionRequest.xlFileFields[fieldId] = {};
      const file: File = xlFiles[ fieldId ] as File;
      formActionRequest.xlFileFields[fieldId].totalParts = Math.ceil(file.size / this.XLFILE_CHUNK_SIZE);
      formActionRequest.xlFileFields[fieldId].fileSize = file.size;
      formActionRequest.xlFileFields[fieldId].fileName = file.name;
      formActionRequest.xlFileFields[fieldId].fileType = file.type;
      formActionRequest.xlFileFields[fieldId].crcCheckSum = -1; // TODO: use crc-32 to generate checksum
    });

    // Run IService.execute to save the record
    // Return transaction results, handle field edits in other fields in the form
    // The form component will send the Start upload once this is complete
    if (Object.keys(files).length > 0) {
      return this.makeFileRequest(formName, url, files, data);
    }
    return <Observable<Response>> this.http.post(url, JSON.stringify(data)).pipe(
      catchError(error => throwError(error)));
  }

  private executeRequest(url: string, formData: FormData, data: any, totalPostSize: number): Observable<LMActionResult> {
    const dialogAfterOpen = new Subject<void>();
    const dialog = this.lmModalWrapperService.modal(LMFileUploadProgressComponent);

    dialog.title(Soho.Locale.translate('FileUploadProgress'));
    dialog.noRefocus(true);
    dialog.afterOpen(() => {
      dialogAfterOpen.next();
      dialogAfterOpen.complete();
    });

    const extraJSONData = JSON.stringify(data);
    formData.append(this.EXTRA_JSON_DATA, extraJSONData);
    totalPostSize += new Blob([ extraJSONData ]).size;

    const headers = new HttpHeaders();

    dialog.open();

    return dialogAfterOpen.pipe(
      // start file upload along with entire form data
      flatMap(() => this.http.post(url, formData, { headers, reportProgress: true, observe: 'events' })),
      filter((event: HttpEvent<LMActionResult>) => [ HttpEventType.UploadProgress, HttpEventType.Response ].includes(event.type)),
      // compute and set % of file uploaded:
      tap((event: HttpEvent<LMActionResult>) => {
        if (event.type === HttpEventType.UploadProgress) {
          const percent = Math.round(100 * event.loaded / event.total);
          if (dialog.componentDialog) {
            dialog.componentDialog.progress = percent;
          }
          // close modal as soon as 100% uploaded
          if (percent >= 100) {
            dialog.close();
            this.lmBusyIndicatorService.showBusyIndicator();
          }
        }
      }),
      // last emitted value is the http response
      last((event: HttpEvent<LMActionResult>) => event.type === HttpEventType.Response),
      map((event: HttpResponse<LMActionResult>) => event.body),
      // close modal then propagate error
      catchError(error => {
        dialog.close();
        this.lmBusyIndicatorService.showBusyIndicator();

        //throwing either error 500 or 0 when post size exceeded
        if ((error.status === 500 || error.status === 0) && (totalPostSize > this.MAX_POST_SIZE_CONSTANT)) {
          error.message = Soho.Locale.translate('FileUploadError');
        }
        return throwError(error);
      }),
      // just make sure that whatever happens the file upload dialog would always get closed.
      finalize(() => {
        dialog.close();
      })
    );
  }

  private buildFormData(formName: string, formData: FormData, files: LMModelMultiPartFields, xlFiles?: LMModelXLMultiPartFields): number {
    let keys: Array<string> = Object.keys(files);
    let totalPostSize = 0;

    keys.forEach((fieldName) => {
      const file: File | Blob = files[ fieldName ];
      totalPostSize += file ? file.size : 0;

      formData.append(formName + '_' + fieldName, file);
    });

    if (xlFiles) {
      keys = Object.keys(xlFiles);
      keys.forEach((fieldName) => {
        const file: File | Blob = xlFiles[ fieldName ];
        totalPostSize += file ? file.size : 0;

        formData.append(formName + '_' + fieldName, file);
      });
    }
    return totalPostSize;
  }

  private getTotalPostSize(files: LMModelMultiPartFields, xlFiles: LMModelXLMultiPartFields, data): number {
    let keys: Array<string> = Object.keys(files);
    let totalPostSize = 0;

    keys.forEach((fieldName) => {
      const file: File | Blob = files[ fieldName ];
      totalPostSize += file ? file.size : 0;
    });

    keys = Object.keys(xlFiles);
    keys.forEach((fieldName) => {
      const file: File | Blob = xlFiles[ fieldName ];
      totalPostSize += file ? file.size : 0;
    });

    totalPostSize += new Blob([ JSON.stringify(data) ]).size;
    return totalPostSize;
  }
}
